import { ref } from 'vue'
import { listAgents, type CpAgent } from '@/services/wootdashAgents'
import { getPublicSettings, type AiAgentAvatar } from '@/services/wootdashSystemSettings'
import { toMediaUrl } from '@/utils/media'
import { avatarColor, initials } from '@/composables/useHelpers'
import { agentAvatarUrl } from '@/composables/useAgentAvatars'

// Quem falou em cada mensagem: foto + nome. Agente = pelo senderId (cada atendente com a própria foto); Bot com
// senderId = agente de IA (bloco Agente IA); Bot sem = o bot do fluxo (avatar em Configurações Gerais › Bot);
// Contact = a foto do contato, quando tivermos uma (o WhatsApp não entrega a foto de perfil). Sem foto: iniciais.
// Estado de módulo: carrega uma vez por sessão e serve o painel lateral, o Modo Foco e o celular.

export interface MessageSender {
  name: string
  avatarUrl: string | null
  initials: string
  color: string
}

const agents = ref<CpAgent[]>([])
const aiAgents = ref<AiAgentAvatar[]>([])
const botAvatar = ref<string | null>(null)
let loadedFor = ''
let loading: Promise<void> | null = null

/** Chame ao abrir uma conversa: na 1ª vez busca agentes e avatares; depois é imediato. */
export function loadMessageAvatars(token: string): Promise<void> {
  if (!token) return Promise.resolve()
  if (loadedFor === token) return loading ?? Promise.resolve()
  loadedFor = token
  loading = (async () => {
    const [list, settings] = await Promise.all([listAgents(token).catch(() => ({ agents: [] as CpAgent[] })), getPublicSettings(token)])
    agents.value = list.agents
    aiAgents.value = settings?.aiAgents ?? []
    botAvatar.value = settings?.botAvatar ?? null
  })()
  return loading
}

/** Sair da conta: a próxima pessoa na mesma aba carrega de novo. */
export function resetMessageAvatars() {
  loadedFor = ''
  loading = null
  agents.value = []
  aiAgents.value = []
  botAvatar.value = null
}

function build(name: string, avatarUrl: string | null | undefined): MessageSender {
  return { name, avatarUrl: toMediaUrl(avatarUrl ?? null), initials: initials(name), color: avatarColor(name) }
}

export function senderOf(
  msg: { senderType: string; senderId: string | null },
  contact: { name: string | null | undefined; avatarUrl: string | null | undefined },
): MessageSender {
  if (msg.senderType === 'Agent') {
    const a = msg.senderId ? agents.value.find(x => x.id === msg.senderId) : undefined
    const sender = build(a ? `${a.firstName} ${a.lastName}`.trim() : 'Agente', a?.avatarUrl)
    // Mapa compartilhado primeiro: reflete na hora quem acabou de trocar a foto (Meu perfil).
    return { ...sender, avatarUrl: agentAvatarUrl(msg.senderId) ?? sender.avatarUrl }
  }
  if (msg.senderType === 'Bot') {
    const ai = msg.senderId ? aiAgents.value.find(x => x.id === msg.senderId) : undefined
    if (ai) return build(ai.name, ai.avatarUrl ?? botAvatar.value)
    return build('Bot', botAvatar.value)
  }
  return build(contact.name || '?', contact.avatarUrl)
}

/** Mesma pessoa em mensagens seguidas mostra o avatar só na última (dois agentes diferentes não se juntam). */
export function sameSender(a: { senderType: string; senderId: string | null } | undefined, b: { senderType: string; senderId: string | null }) {
  return !!a && a.senderType === b.senderType && (a.senderId ?? null) === (b.senderId ?? null)
}
